import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { handleSimpleFormNav } from "@/lib/keyboardNav";
import { Users, Plus, Pencil, Trash2, Bike, ClipboardList, AlertCircle, Star, FileText, Upload, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PaginationBar, usePagination } from "@/components/Pagination";

const emptyCust = { name: "", phone: "", email: "", address: "", date_of_birth: "", anniversary_date: "", notes: "" };
const DOC_KINDS = ["RC Book", "Insurance", "ID Proof", "Other"];

export default function CustomerMaster() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const cid = params.get("cid");

  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(emptyCust);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState("profile");
  const [docOpen, setDocOpen] = useState(false);
  const [newDoc, setNewDoc] = useState({ kind: "RC Book", name: "", mime: "", data: "" });

  const canEdit = ["owner", "manager", "cashier", "receptionist"].includes(user.role);
  const canDelete = ["owner", "manager"].includes(user.role);

  const load = () => api.get("/customers").then(r => setList(r.data)).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!cid) { setDetail(null); return; }
    api.get(`/customers/${cid}/360`).then(r => setDetail(r.data)).catch(e => toast.error(formatErr(e)));
  }, [cid]);

  const save = async () => {
    try {
      const payload = { ...f,
        email: f.email || null, address: f.address || null,
        date_of_birth: f.date_of_birth || null, anniversary_date: f.anniversary_date || null,
        notes: f.notes || null };
      if (editId) await api.patch(`/customers/${editId}`, payload);
      else await api.post("/customers", payload);
      toast.success(editId ? "Updated" : "Customer added");
      setF(emptyCust); setEditId(null); setOpen(false); load();
      if (cid && editId === cid) api.get(`/customers/${cid}/360`).then(r => setDetail(r.data));
    } catch (e) { toast.error(formatErr(e)); }
  };
  const edit = (c) => { setF({ ...emptyCust, ...c }); setEditId(c.id); setOpen(true); };
  const del = async (id) => {
    if (!confirm("Delete this customer? This is permanent.")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Deleted"); load(); setParams({}); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const filtered = list.filter(c => !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.phone.includes(q));
  const pg = usePagination(filtered, q);

  // --------- List view ---------
  if (!cid) return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Users className="w-3 h-3"/> CRM · Customer 360°</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Customer Master</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"/>
            <Input data-testid="cust-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone…" className="pl-8 rounded-sm bg-secondary border-border w-64"/>
          </div>
          {canEdit && (
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(emptyCust); setEditId(null); } }}>
              <DialogTrigger asChild>
                <Button data-testid="add-cust-btn" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs">
                  <Plus className="w-4 h-4 mr-1"/> New Customer
                </Button>
              </DialogTrigger>
              <CustomerFormDialog f={f} setF={setF} onSave={save} editId={editId}/>
            </Dialog>
          )}
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="text-left p-3">Name</th><th className="text-left p-3">Phone</th>
              <th className="text-left p-3">Email</th>
              <th className="text-right p-3">Loyalty</th><th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No customers</td></tr>}
            {pg.pageItems.map(c => (
              <tr key={c.id} data-testid={`cust-row-${c.id}`} className="border-t border-border hover:bg-secondary/50">
                <td className="p-3"><button onClick={() => setParams({ cid: c.id })} className="text-primary font-semibold hover:underline">{c.name}</button></td>
                <td className="p-3 font-mono text-xs">{c.phone}</td>
                <td className="p-3 text-xs text-muted-foreground">{c.email || "-"}</td>
                <td className="p-3 text-right font-mono text-xs text-primary">{c.loyalty_points || 0}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setParams({ cid: c.id })} className="h-7 text-xs text-primary">Open →</Button>
                  {canEdit && <Button data-testid={`edit-cust-${c.id}`} size="sm" variant="ghost" onClick={() => edit(c)} className="h-7 w-7 p-0"><Pencil className="w-3 h-3"/></Button>}
                  {canDelete && <Button data-testid={`del-cust-${c.id}`} size="sm" variant="ghost" onClick={() => del(c.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3"/></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <PaginationBar pg={pg}/>
      </div>
    </div>
  );

  // --------- Detail view (360°) ---------
  if (!detail) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const c = detail.customer;
  const uploadDoc = async () => {
    if (!newDoc.name || !newDoc.data) { toast.error("Pick a file"); return; }
    try {
      await api.post(`/customers/${cid}/documents`, newDoc);
      toast.success("Document uploaded");
      setDocOpen(false); setNewDoc({ kind: "RC Book", name: "", mime: "", data: "" });
      api.get(`/customers/${cid}/360`).then(r => setDetail(r.data));
    } catch (e) { toast.error(formatErr(e)); }
  };
  const downloadDoc = async (did) => {
    try {
      const { data } = await api.get(`/customers/${cid}/documents/${did}/download`);
      const a = document.createElement("a");
      a.href = `data:${data.mime};base64,${data.data}`;
      a.download = data.name; a.click();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const deleteDoc = async (did) => {
    if (!confirm("Delete this document?")) return;
    try { await api.delete(`/customers/${cid}/documents/${did}`); toast.success("Deleted");
      api.get(`/customers/${cid}/360`).then(r => setDetail(r.data)); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const onFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const rd = new FileReader();
    rd.onload = () => setNewDoc(x => ({ ...x, name: file.name, mime: file.type,
      data: rd.result.toString().split(",")[1] }));
    rd.readAsDataURL(file);
  };

  return (
    <div className="p-8">
      <button onClick={() => setParams({})} className="text-xs uppercase tracking-wider text-muted-foreground mb-2 hover:text-primary">← Back to Customer Master</button>
      <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">CRM · Customer 360°</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">{c.name}</h1>
          <div className="text-xs text-muted-foreground mt-1 font-mono">{c.phone}{c.email ? " · " + c.email : ""}</div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <StatBox label="Vehicles" value={detail.vehicles.length}/>
          <StatBox label="Jobs" value={detail.jobs.length}/>
          <StatBox label="Loyalty" value={detail.loyalty_points} tone="text-primary"/>
        </div>
      </div>
      {detail.outstanding_total > 0 && (
        <div className="border border-destructive/40 bg-destructive/10 text-destructive rounded-sm p-3 mb-4 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4"/> Outstanding due: <span className="font-mono font-semibold">{inr(detail.outstanding_total)}</span>
        </div>
      )}

      <div className="flex gap-1 mb-6 border-b border-border overflow-x-auto">
        {[["profile", "Profile", Users], ["vehicles", "Vehicles", Bike], ["history", "Service History", ClipboardList], ["dues", "Outstanding", AlertCircle], ["loyalty", "Loyalty", Star], ["documents", "Documents", FileText]].map(([k, l, I]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs uppercase tracking-wider flex items-center gap-1 whitespace-nowrap transition-colors ${tab === k ? "text-primary border-b-2 border-primary font-semibold" : "text-muted-foreground hover:text-foreground"}`}>
            <I className="w-3 h-3"/> {l}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ProfileRow label="Name" value={c.name}/>
          <ProfileRow label="Phone" value={c.phone}/>
          <ProfileRow label="Email" value={c.email}/>
          <ProfileRow label="Address" value={c.address}/>
          <ProfileRow label="Date of Birth" value={c.date_of_birth}/>
          <ProfileRow label="Anniversary" value={c.anniversary_date}/>
          <ProfileRow label="Notes" value={c.notes} full/>
          <div className="col-span-2 flex gap-2">
            {canEdit && <Button data-testid="edit-detail-btn" onClick={() => { setF({ ...emptyCust, ...c }); setEditId(c.id); setOpen(true); }} variant="outline" className="rounded-sm text-xs uppercase tracking-wider"><Pencil className="w-3 h-3 mr-1"/> Edit Profile</Button>}
            {canDelete && <Button data-testid="del-detail-btn" onClick={() => del(c.id)} variant="outline" className="rounded-sm text-xs uppercase tracking-wider text-destructive"><Trash2 className="w-3 h-3 mr-1"/> Delete</Button>}
          </div>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setF(emptyCust); setEditId(null); } }}>
            <CustomerFormDialog f={f} setF={setF} onSave={save} editId={editId}/>
          </Dialog>
        </div>
      )}

      {tab === "vehicles" && (
        <div>
          <div className="flex justify-end mb-2">
            {canEdit && <Button size="sm" onClick={() => nav("/vehicles")} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-wider text-xs h-8"><Plus className="w-3 h-3 mr-1"/> Add Vehicle</Button>}
          </div>
          <SimpleTable
            columns={["Registration", "Make", "Model", "Year", "Odometer"]}
            rows={detail.vehicles.map(v => [v.registration, v.make, v.model, v.year || "-", v.odometer || 0])}
            empty="No vehicles linked"
          />
        </div>
      )}

      {tab === "history" && (
        <SimpleTable
          columns={["Job #", "Date", "Vehicle", "Status", "Complaints", "Amount"]}
          rows={detail.jobs.map(j => [j.job_number, (j.created_at || "").slice(0, 10),
            (detail.vehicles.find(v => v.id === j.vehicle_id)?.registration) || "-",
            j.status, (j.complaints || "").slice(0, 60), inr((j.labour_charges || 0) + (j.parts || []).reduce((s, p) => s + (p.price * p.qty), 0))])}
          empty="No service history yet"
        />
      )}

      {tab === "dues" && (
        <SimpleTable
          columns={["Invoice #", "Date", "Total", "Paid", "Due"]}
          rows={detail.outstanding.map(inv => [inv.invoice_number, (inv.created_at || "").slice(0, 10),
            inr(inv.total), inr(inv.paid_amount || 0), inr(inv.due)])}
          empty="No outstanding invoices — all clear!"
        />
      )}

      {tab === "loyalty" && (
        <SimpleTable
          columns={["Date", "Type", "Points", "Reason"]}
          rows={detail.loyalty_history.map(l => [(l.at || "").slice(0, 10), l.type,
            <span className={l.delta > 0 ? "text-green-500" : "text-destructive"}>{l.delta > 0 ? "+" : ""}{l.delta}</span>,
            l.invoice_id ? `Invoice ${l.invoice_id.slice(0, 8)}` : "-"])}
          empty="No loyalty activity yet"
        />
      )}

      {tab === "documents" && (
        <div>
          <div className="flex justify-end mb-2">
            {canEdit && (
              <Dialog open={docOpen} onOpenChange={setDocOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="upload-doc-btn" size="sm" className="rounded-sm bg-primary text-primary-foreground uppercase tracking-wider text-xs h-8"><Upload className="w-3 h-3 mr-1"/> Upload</Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm bg-card border-border max-w-md">
                  <DialogHeader><DialogTitle className="font-display uppercase">Upload Document</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Type</Label>
                      <select data-testid="doc-kind" value={newDoc.kind} onChange={(e) => setNewDoc(x => ({ ...x, kind: e.target.value }))} className="mt-1 w-full h-10 rounded-sm bg-secondary border border-border px-3 text-sm">
                        {DOC_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </div>
                    <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">File</Label><Input data-testid="doc-file" type="file" onChange={onFile} className="mt-1 rounded-sm bg-secondary border-border"/></div>
                    {newDoc.name && <div className="text-xs text-muted-foreground">Picked: {newDoc.name}</div>}
                    <Button data-testid="save-doc-btn" onClick={uploadDoc} disabled={!newDoc.data} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">Upload</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <SimpleTable
            columns={["Type", "Name", "Uploaded By", "Date", "Actions"]}
            rows={detail.documents.map(d => [d.kind, d.name, d.uploaded_by, (d.uploaded_at || "").slice(0, 10),
              <div>
                <Button size="sm" variant="ghost" onClick={() => downloadDoc(d.id)} className="h-7 text-xs"><Download className="w-3 h-3 mr-1"/> Get</Button>
                {canDelete && <Button size="sm" variant="ghost" onClick={() => deleteDoc(d.id)} className="h-7 text-xs text-destructive"><Trash2 className="w-3 h-3"/></Button>}
              </div>])}
            empty="No documents on file"
          />
        </div>
      )}
    </div>
  );
}

function CustomerFormDialog({ f, setF, onSave, editId }) {
  return (
    <DialogContent className="rounded-sm bg-card border-border max-w-lg">
      <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Edit" : "New"} Customer</DialogTitle></DialogHeader>
      <div className="space-y-3" onKeyDownCapture={handleSimpleFormNav}>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Full Name</Label><Input data-testid="cf-name" value={f.name} onChange={(e) => setF(x => ({ ...x, name: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Phone</Label><Input data-testid="cf-phone" value={f.phone} onChange={(e) => setF(x => ({ ...x, phone: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
        </div>
        <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label><Input data-testid="cf-email" value={f.email || ""} onChange={(e) => setF(x => ({ ...x, email: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
        <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Address</Label><Textarea data-testid="cf-address" value={f.address || ""} onChange={(e) => setF(x => ({ ...x, address: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Date of Birth</Label><Input data-testid="cf-dob" type="date" value={f.date_of_birth || ""} onChange={(e) => setF(x => ({ ...x, date_of_birth: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
          <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Anniversary</Label><Input data-testid="cf-anni" type="date" value={f.anniversary_date || ""} onChange={(e) => setF(x => ({ ...x, anniversary_date: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border"/></div>
        </div>
        <div><Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Notes</Label><Textarea data-testid="cf-notes" value={f.notes || ""} onChange={(e) => setF(x => ({ ...x, notes: e.target.value }))} className="mt-1 rounded-sm bg-secondary border-border" rows={2}/></div>
        <Button data-testid="save-cust-btn" onClick={onSave} disabled={!f.name || !f.phone} className="w-full rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
          {editId ? "Update" : "Save"}
        </Button>
      </div>
    </DialogContent>
  );
}

function StatBox({ label, value, tone = "" }) {
  return (
    <div className="border border-border bg-card rounded-sm p-3 min-w-24">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className={`font-display font-black text-2xl ${tone}`}>{value}</div>
    </div>
  );
}

function ProfileRow({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="text-sm mt-1">{value || <span className="text-muted-foreground italic">Not set</span>}</div>
    </div>
  );
}

function SimpleTable({ columns, rows, empty }) {
  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <tr>{columns.map((c, i) => <th key={i} className={`p-3 ${i === columns.length - 1 ? "text-right" : "text-left"}`}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="p-6 text-center text-muted-foreground">{empty}</td></tr>}
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              {r.map((cell, j) => <td key={j} className={`p-3 text-xs ${j === r.length - 1 ? "text-right" : ""}`}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
